// Back face of the event card's flip: door stats styled after the venue
// security page (SecurityCheckinPage.js on .136) — "best of both" per user:
// the page's visual language (tinted 3-stat boxes, % in section headers,
// progress bars, cyan/blue/pink occupancy row, pulsing live dot, its exact
// labels) + our card's additions (slim M/F ratio bar, light/dark theming,
// Shield flip-back in the title row, and a history/recap face the page
// doesn't have).
//
// LIVE face (payload from POST /occupancy):  Live Occupancy → Total Arrived →
//   Tickets → Tables, footer = last refresh time.
// HISTORY face (stored occupancy_data snapshot): night recap (Peak +
//   Through-the-door) replaces the live counter, then the same sections with
//   the night's final numbers; footer = "Captured {date}".
// Sections with no data are not rendered — no fabricated zeros.
import React, { useState } from 'react';
import { Shield, Users, Ticket, Table2, Activity, CheckCircle2, Clock, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// Tinted stat box (the page's grid cells). tone drives tint + number color.
const TONES = {
  gray: 'bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white',
  green: 'bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400',
  amber: 'bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400',
  cyan: 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-600 dark:text-cyan-400',
  blue: 'bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400',
  pink: 'bg-pink-500/10 border border-pink-500/20 text-pink-600 dark:text-pink-400',
};

const StatBox = ({ label, value, tone = 'gray', check }) => (
  <div className={`rounded-lg py-2 text-center ${TONES[tone]}`}>
    <div className="flex items-center justify-center gap-1">
      {check && value != null && <CheckCircle2 className="w-4 h-4" />}
      <span className="text-xl font-bold">{value ?? '—'}</span>
    </div>
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
  </div>
);

const Bar = ({ value, className }) => (
  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
    <div className={`h-full transition-all duration-500 ${className}`} style={{ width: `${Math.min(100, value)}%` }} />
  </div>
);

// Section panel: icon + title, optional right-side content (% or live dot),
// then children. The page's rounded-xl bordered card.
const Section = ({ icon: Icon, iconClass, title, right, children }) => (
  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-5 h-5 ${iconClass}`} />
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
      {right != null && <span className="ml-auto">{right}</span>}
    </div>
    {children}
  </div>
);

// Slim male/female ratio bar (ours — denser signal under the M/F boxes).
const GenderBar = ({ male, female }) => {
  const tot = (male || 0) + (female || 0);
  if (tot <= 0) return null;
  return (
    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex mt-2">
      <div className="h-full bg-blue-500 dark:bg-blue-400" style={{ width: `${(male / tot) * 100}%` }} />
      <div className="h-full bg-pink-500 dark:bg-pink-400" style={{ width: `${(female / tot) * 100}%` }} />
    </div>
  );
};

const PctLabel = ({ value, className }) => (
  <span className={`text-lg font-bold ${className}`}>{value}%</span>
);

const Shell = ({ title, badge, children, onFlipBack }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors p-3">
    <div className="flex items-start justify-between gap-2 mb-1 px-1">
      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{title}</h3>
      <button
        onClick={(e) => { e.stopPropagation(); onFlipBack(); }}
        title="Back to sales view"
        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors whitespace-nowrap"
      ><Shield className="w-3 h-3" /> Sales</button>
    </div>
    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2 px-1">
      <Shield className="w-3 h-3 text-purple-500" /><span>{badge}</span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

// ---- The page's four sections (shared by both faces) ----

const OccupancySection = ({ occ, live }) => occ && (
  <Section
    icon={Activity} iconClass="text-cyan-500 dark:text-cyan-400" title="Live Occupancy"
    right={live ? <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" /> : null}
  >
    <div className="grid grid-cols-3 gap-2">
      <StatBox label="Total" value={occ.current} tone="cyan" />
      <StatBox label="Male" value={occ.male} tone="blue" />
      <StatBox label="Female" value={occ.female} tone="pink" />
    </div>
    <GenderBar male={occ.male} female={occ.female} />
  </Section>
);

const TotalsSection = ({ totals, final }) => totals && (
  <Section
    icon={Users} iconClass="text-purple-500 dark:text-purple-400" title="Total Arrived"
    right={<PctLabel value={pct(totals.arrived, totals.expected)} className="text-purple-600 dark:text-purple-400" />}
  >
    <Bar value={pct(totals.arrived, totals.expected)} className="bg-gradient-to-r from-purple-600 to-purple-400" />
    <div className="grid grid-cols-3 gap-2">
      <StatBox label="Expected" value={totals.expected} />
      <StatBox label="Arrived" value={totals.arrived} tone="green" check />
      <StatBox label={final ? 'No Show' : 'Remaining'} value={totals.remaining} tone="amber" />
    </div>
  </Section>
);

const TicketsSection = ({ tickets, final }) => {
  // Chevron expands per-tier scanned counts (from the check-ins report's Rate
  // name column). Hidden when the payload has no breakdown (older stored
  // recaps, header drift).
  const [showTiers, setShowTiers] = useState(false);
  if (!tickets) return null;
  const tiers = tickets.byTier || [];
  const maxTier = tiers.length ? tiers[0].scanned : 0;
  return (
  <Section
    icon={Ticket} iconClass="text-purple-500 dark:text-purple-400"
    title={tiers.length ? (
      <button
        onClick={(e) => { e.stopPropagation(); setShowTiers((v) => !v); }}
        title="Check-in counts per tier"
        className="flex items-center gap-0.5 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
      >
        Tickets
        {showTiers ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
    ) : 'Tickets'}
    right={tickets.checkedIn != null
      ? <PctLabel value={pct(tickets.checkedIn, tickets.sold)} className="text-purple-600 dark:text-purple-400" />
      : null}
  >
    {tickets.checkedIn != null && <Bar value={pct(tickets.checkedIn, tickets.sold)} className="bg-purple-500" />}
    <div className="grid grid-cols-3 gap-2">
      <StatBox label="Sold" value={tickets.sold} />
      <StatBox label="Scanned" value={tickets.checkedIn} tone="green" check />
      <StatBox label={final ? 'No Show' : 'Remaining'} value={tickets.checkedIn != null ? tickets.remaining : null} tone="amber" />
    </div>
    {showTiers && tiers.length > 0 && (
      <div className="mt-2 space-y-1">
        {tiers.map((t) => {
          // sold known → bar + % = that tier's check-in rate; sold unknown
          // (older recaps, detail not loaded) → bar ∝ share of scans, count only.
          const hasSold = t.sold != null;
          const tierPct = hasSold ? pct(t.scanned, t.sold) : null;
          return (
            <div
              key={t.name}
              className="py-1"
              title={hasSold ? `${t.name}: ${t.scanned} of ${t.sold} scanned, ${Math.max(0, t.sold - t.scanned)} not scanned` : t.name}
            >
              <div className="flex items-baseline justify-between gap-2 text-xs mb-1">
                <span className="min-w-0 truncate text-gray-700 dark:text-gray-300">{t.name}</span>
                <span className="whitespace-nowrap tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {hasSold ? (
                    <>
                      {t.scanned}<span className="font-normal text-gray-400">/{t.sold}</span>
                      <span className="ml-1.5 font-bold text-purple-600 dark:text-purple-400">{tierPct}%</span>
                    </>
                  ) : t.scanned}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 dark:bg-purple-400"
                  style={{ width: `${hasSold ? Math.min(100, tierPct) : (maxTier ? (t.scanned / maxTier) * 100 : 0)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Section>
  );
};

const TablesSection = ({ tables, final }) => {
  if (!tables) return null;
  // Reservation count rides beside the title; stat boxes stay guest counts.
  const count = tables.count ?? (Array.isArray(tables.detail) ? tables.detail.length : null);
  return (
  <Section
    icon={Table2} iconClass="text-blue-500 dark:text-blue-400"
    title={count != null ? `Tables · ${count}` : 'Tables'}
    right={<PctLabel value={pct(tables.seated, tables.total)} className="text-blue-600 dark:text-blue-400" />}
  >
    <Bar value={pct(tables.seated, tables.total)} className="bg-blue-500" />
    <div className="grid grid-cols-3 gap-2">
      <StatBox label="Expected" value={tables.total} />
      <StatBox label="Seated" value={tables.seated} tone="green" check />
      <StatBox label={final ? 'No Show' : 'Remaining'} value={tables.remaining} tone="amber" />
    </div>
  </Section>
  );
};

// History-only: the night recap that replaces the live counter — Peak vs
// capacity + through-the-door totals from the Tallyfi day summary.
const RecapSection = ({ ds }) => {
  if (!ds) return null;
  const peak = ds.peak || {};
  const tp = ds.throughput || {};
  if (!peak.count && !tp.total) return null;
  return (
    <Section
      icon={TrendingUp} iconClass="text-purple-500 dark:text-purple-400" title="Peak"
      right={peak.count ? <span className="text-lg font-bold text-purple-600 dark:text-purple-400">{peak.count}</span> : null}
    >
      {peak.count > 0 && (
        <>
          <Bar value={peak.percentage || 0} className="bg-gradient-to-r from-purple-600 to-purple-400" />
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{peak.percentage}% of {ds.capacity} capacity</div>
        </>
      )}
      {tp.total > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <StatBox label="Through door" value={tp.total} tone="cyan" />
            <StatBox label="Male" value={tp.male} tone="blue" />
            <StatBox label="Female" value={tp.female} tone="pink" />
          </div>
          <GenderBar male={tp.male} female={tp.female} />
        </>
      )}
    </Section>
  );
};

const Footer = ({ icon: Icon = Clock, children }) => (
  <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 pt-1">
    <Icon className="w-3.5 h-3.5" /><span>{children}</span>
  </div>
);

// ---- LIVE face ----
function LiveFace({ event, data, onFlipBack }) {
  return (
    <Shell title={event?.title || data.eventName} badge="Live door" onFlipBack={onFlipBack}>
      <OccupancySection occ={data.occupancy} live />
      <TotalsSection totals={data.totals} />
      <TicketsSection tickets={data.tickets} />
      <TablesSection tables={data.tables} />
      {data.lastUpdated && <Footer>{new Date(data.lastUpdated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</Footer>}
    </Shell>
  );
}

// ---- HISTORY face ----
function HistoryFace({ event, occ, onFlipBack }) {
  return (
    <Shell title={event?.title} badge="Door recap, final" onFlipBack={onFlipBack}>
      <RecapSection ds={occ.daySummary} />
      <TotalsSection totals={occ.totals} final />
      <TicketsSection tickets={occ.tickets} final />
      <TablesSection tables={occ.tables} final />
      {occ.snapshotAt && <Footer>Captured {new Date(occ.snapshotAt).toLocaleDateString()}</Footer>}
    </Shell>
  );
}

const OccupancyCard = ({ event, data, historical, onFlipBack }) => {
  if (historical) return <HistoryFace event={event} occ={historical} onFlipBack={onFlipBack} />;
  return <LiveFace event={event} data={data} onFlipBack={onFlipBack} />;
};

export default OccupancyCard;
